import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Slider, TextField, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ROUTES } from '../../types/global/routes.types';
import { CREATOR_PAYOUT_SUBSCRIBE_UPSELL_COPY } from '../../constants/creatorPayoutSubscribeUpsellCopy';
import {
    APP_INSTALL_CALCULATOR_MAX,
    APP_INSTALL_EARNINGS_BENCHMARK,
    TIER_FOLLOWER_BANDS,
    estimateAppInstallEarnings,
    formatCpaCents,
    formatCpaDollarsFromCents,
    normalizeCircuulCpaTier,
    tierFromFollowerCount,
    type CircuulCpaTier,
    type IAppInstallEarningsEstimate,
    clampInstallCount,
} from '../../utils/circuulCpaRates';

function FeeRow({
    label,
    value,
    muted,
    highlight,
}: {
    label: string;
    value: string;
    muted?: boolean;
    highlight?: boolean;
}) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, py: 0.35 }}>
            <Typography
                sx={{
                    fontSize: 12,
                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                    color: muted ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.78)',
                }}>
                {label}
            </Typography>
            <Typography
                sx={{
                    fontSize: 12,
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    color: highlight ? '#ffc965' : muted ? 'rgba(255,255,255,0.55)' : '#fff',
                    textAlign: 'right',
                }}>
                {value}
            </Typography>
        </Box>
    );
}

export function CampaignAppInstallPayoutPanel({
    estimate,
    kindLabel = 'install',
    basePayCents = 0,
    subscribeRoute = ROUTES.ACCOUNT_SUBSCRIPTION,
    onViewPlansClick,
}: {
    estimate: IAppInstallEarningsEstimate;
    kindLabel?: string;
    basePayCents?: number;
    subscribeRoute?: string;
    onViewPlansClick?: () => void;
}) {
    const campaignTier = normalizeCircuulCpaTier(estimate.tier);
    const [tier, setTier] = useState<CircuulCpaTier>(campaignTier);
    const [installs, setInstalls] = useState(
        clampInstallCount(estimate.benchmarkInstalls || APP_INSTALL_EARNINGS_BENCHMARK),
    );
    const [installsInput, setInstallsInput] = useState(String(installs));
    const [followersInput, setFollowersInput] = useState('');

    useEffect(() => {
        setTier(normalizeCircuulCpaTier(estimate.tier));
    }, [estimate.tier]);

    useEffect(() => {
        const next = clampInstallCount(estimate.benchmarkInstalls || APP_INSTALL_EARNINGS_BENCHMARK);
        setInstalls(next);
        setInstallsInput(String(next));
    }, [estimate.benchmarkInstalls]);

    const live = useMemo(
        () =>
            estimateAppInstallEarnings({
                influencerTier: tier,
                creatorSubscribed: estimate.creatorSubscribed,
                benchmarkInstalls: installs,
            }),
        [tier, estimate.creatorSubscribed, installs],
    );

    const showUpsell = !live.creatorSubscribed && live.extraIfSubscribedCents > 0;
    const plural = `${kindLabel}s`;

    const applyInstalls = (raw: number) => {
        const next = clampInstallCount(raw);
        setInstalls(next);
        setInstallsInput(String(next));
    };

    const applyFollowers = (raw: string) => {
        setFollowersInput(raw);
        const digits = raw.replace(/[^\d]/g, '');
        if (!digits) return;
        setTier(tierFromFollowerCount(Number(digits)));
    };

    return (
        <Box
            sx={{
                mt: 2,
                p: 2,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
            }}>
            <Typography
                sx={{
                    fontSize: 10,
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    textTransform: 'uppercase',
                    letterSpacing: '0.14em',
                    color: 'rgba(255,255,255,0.5)',
                    mb: 1.25,
                }}>
                Earnings calculator
            </Typography>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 1.25,
                    mb: 1.5,
                }}>
                <TextField
                    size="small"
                    label="Your followers"
                    placeholder="e.g. 12000"
                    value={followersInput}
                    onChange={e => applyFollowers(e.target.value)}
                    inputProps={{ inputMode: 'numeric', 'aria-label': 'Follower count' }}
                    helperText="Maps to a rate tier"
                    FormHelperTextProps={{ sx: { color: 'rgba(255,255,255,0.4)', mx: 0 } }}
                    sx={calcFieldSx}
                />
                <TextField
                    size="small"
                    label={`${kindLabel.charAt(0).toUpperCase()}${kindLabel.slice(1)}s`}
                    value={installsInput}
                    onChange={e => {
                        const next = e.target.value.replace(/[^\d]/g, '');
                        setInstallsInput(next);
                        if (next === '') return;
                        applyInstalls(Number(next));
                    }}
                    onBlur={() => applyInstalls(Number(installsInput || 0))}
                    inputProps={{
                        inputMode: 'numeric',
                        min: 0,
                        max: APP_INSTALL_CALCULATOR_MAX,
                        'aria-label': `Number of ${plural}`,
                    }}
                    helperText={`0–${APP_INSTALL_CALCULATOR_MAX.toLocaleString()}`}
                    FormHelperTextProps={{ sx: { color: 'rgba(255,255,255,0.4)', mx: 0 } }}
                    sx={calcFieldSx}
                />
            </Box>

            <Typography
                sx={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', mb: 0.5, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                {plural.charAt(0).toUpperCase() + plural.slice(1)}: {installs.toLocaleString()}
            </Typography>
            <Slider
                value={installs}
                min={0}
                max={APP_INSTALL_CALCULATOR_MAX}
                step={10}
                onChange={(_, v) => applyInstalls(Array.isArray(v) ? v[0] : v)}
                sx={{
                    color: '#ffc965',
                    mb: 1.5,
                    '& .MuiSlider-rail': { opacity: 0.25 },
                }}
            />

            <Typography
                sx={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', mb: 0.75, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                Rate tier
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
                {TIER_FOLLOWER_BANDS.map(band => {
                    const selected = tier === band.id;
                    return (
                        <Button
                            key={band.id}
                            size="small"
                            onClick={() => setTier(band.id)}
                            sx={{
                                textTransform: 'none',
                                borderRadius: 999,
                                px: 1.25,
                                py: 0.5,
                                minWidth: 0,
                                fontSize: 11,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                color: selected ? '#392700' : 'rgba(255,255,255,0.8)',
                                bgcolor: selected ? '#ffc965' : 'rgba(255,255,255,0.06)',
                                border: selected ? '1px solid #ffc965' : '1px solid rgba(255,255,255,0.12)',
                                '&:hover': {
                                    bgcolor: selected ? '#ffd78a' : 'rgba(255,255,255,0.1)',
                                },
                            }}>
                            {band.label}
                            <Box component="span" sx={{ ml: 0.5, opacity: 0.7, fontWeight: 500 }}>
                                {band.reach}
                            </Box>
                        </Button>
                    );
                })}
            </Box>

            <FeeRow label="Base pay" value={formatCpaDollarsFromCents(basePayCents)} />
            <FeeRow label={`Cost per ${kindLabel} (CPI)`} value={formatCpaCents(live.cpiAppliedCents)} highlight />
            <FeeRow
                label={`Estimated additional at ${live.benchmarkInstalls.toLocaleString()} ${plural}`}
                value={formatCpaDollarsFromCents(live.earningsAppliedCents)}
                highlight
            />
            <FeeRow
                label="Estimated total"
                value={formatCpaDollarsFromCents(basePayCents + live.earningsAppliedCents)}
                highlight
            />
            <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <FeeRow
                    label="CPI · not subscribed"
                    value={formatCpaCents(live.cpiUnsubscribedCents)}
                    muted={live.creatorSubscribed}
                />
                <FeeRow
                    label={`At ${live.benchmarkInstalls.toLocaleString()} · not subscribed`}
                    value={formatCpaDollarsFromCents(live.earningsUnsubscribedCents)}
                    muted={live.creatorSubscribed}
                />
                <FeeRow
                    label="CPI · subscribed"
                    value={formatCpaCents(live.cpiSubscribedCents)}
                    highlight={live.creatorSubscribed}
                />
                <FeeRow
                    label={`At ${live.benchmarkInstalls.toLocaleString()} · subscribed`}
                    value={formatCpaDollarsFromCents(live.earningsSubscribedCents)}
                    highlight={live.creatorSubscribed}
                />
            </Box>

            {tier !== campaignTier && (
                <Typography sx={{ fontSize: 11, color: 'rgba(255,201,101,0.75)', mt: 1, lineHeight: 1.45 }}>
                    This campaign targets {campaignTier} creators. Showing {tier} rates for comparison.
                </Typography>
            )}

            {showUpsell && (
                <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', mt: 1, lineHeight: 1.45 }}>
                    Subscribe to earn {formatCpaCents(live.cpiSubscribedCents)} per {kindLabel} (
                    {formatCpaDollarsFromCents(live.earningsSubscribedCents)} at{' '}
                    {live.benchmarkInstalls.toLocaleString()}) — keep{' '}
                    {formatCpaDollarsFromCents(live.extraIfSubscribedCents)} more on this estimate.
                </Typography>
            )}
            {live.creatorSubscribed && (
                <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', mt: 1, lineHeight: 1.45 }}>
                    Your subscription rate is active — {formatCpaCents(live.cpiSubscribedCents)} per {kindLabel}.
                </Typography>
            )}
            {showUpsell && (
                <Button
                    {...(onViewPlansClick
                        ? { onClick: onViewPlansClick }
                        : { component: RouterLink, to: subscribeRoute })}
                    size="small"
                    sx={{
                        mt: 1.25,
                        px: 0,
                        minWidth: 0,
                        fontSize: 11,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        textTransform: 'none',
                        color: '#ffc965',
                    }}>
                    {CREATOR_PAYOUT_SUBSCRIBE_UPSELL_COPY.viewPlansButtonLabel}
                </Button>
            )}
        </Box>
    );
}

const calcFieldSx = {
    '& .MuiOutlinedInput-root': {
        color: '#fff',
        fontFamily: FONT_ENUM.MAIN_MEDIUM,
        fontSize: 13,
        bgcolor: 'rgba(255,255,255,0.04)',
        '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
        '&:hover fieldset': { borderColor: 'rgba(255,201,101,0.35)' },
        '&.Mui-focused fieldset': { borderColor: '#ffc965' },
    },
    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.55)', fontSize: 12 },
    '& .MuiInputLabel-root.Mui-focused': { color: '#ffc965' },
};
